import { useRouter } from 'next/router'
import { BaseSyntheticEvent, useContext } from 'react'
import { useForm } from 'react-hook-form'

import { SanityAuthStrings } from '@data/sanity/queries/types/strings'
import { getPageUrl, PageType } from '@lib/routes'
import { ParseStatus } from '@lib/shopify/graphql/client'
import { StringsContext } from '@lib/strings-context'
import { useSubmitSignupForm } from '@lib/user/auth'
import { SignupFormValues } from '@lib/user/types'

import Alert from '@components/alert'
import Button from '@components/buttons/button'
import InputField from '@components/input-field/with-form-register'
import ComplexPortableText from '@components/portable-text/complex'

interface SignupFormProps {
  authStrings: SanityAuthStrings
  className?: string
}

const SignupForm = ({ authStrings, className }: SignupFormProps) => {
  const strings = useContext(StringsContext)
  const router = useRouter()

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<SignupFormValues>()
  const [errorStatus, isError, isLoading, errorMessages, handleSignupSubmit] =
    useSubmitSignupForm()

  const firstNameRegister = register('firstName', {
    required: authStrings.signupFirstNameMissing,
  })
  const lastNameRegister = register('lastName', {
    required: authStrings.signupLastNameMissing,
  })
  const emailRegister = register('email', {
    required: authStrings.signupEmailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: strings.emailInvalid,
    },
  })
  const passwordRegister = register('password', {
    required: authStrings.signupPasswordMissing,
  })

  const isDisabled =
    !!errors.firstName ||
    !!errors.lastName ||
    !!errors.email ||
    !!errors.password

  const onSubmit = async (
    values: SignupFormValues,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault()

    await handleSignupSubmit(values, async () => {
      reset()

      // Redirect to account after logging in
      router.push(getPageUrl(PageType.ACCOUNT_PAGE))
    })
  }

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <InputField
        type="text"
        formRegister={firstNameRegister}
        errorMessage={errorMessages?.firstName ?? errors.firstName?.message}
        label={authStrings.signupFirstName}
        placeholder={authStrings.signupFirstNamePlaceholder}
        className="mb-4"
      />

      <InputField
        type="text"
        formRegister={lastNameRegister}
        errorMessage={errorMessages?.lastName ?? errors.lastName?.message}
        label={authStrings.signupLastName}
        placeholder={authStrings.signupLastNamePlaceholder}
        className="mb-4"
      />

      <InputField
        type="email"
        autoComplete="email"
        formRegister={emailRegister}
        errorMessage={errorMessages?.email ?? errors.email?.message}
        label={authStrings.signupEmail}
        placeholder={authStrings.signupEmailPlaceholder}
        className="mb-4"
      />

      <InputField
        type="password"
        autoComplete="off"
        formRegister={passwordRegister}
        errorMessage={errorMessages?.password ?? errors.password?.message}
        label={authStrings.signupPassword}
        placeholder={authStrings.signupPasswordPlaceholder}
        className="mb-4"
      />

      {isError && (
        <div key="error" className="mt-8">
          <Alert error>
            <ComplexPortableText
              content={
                errorStatus === ParseStatus.INACTIVE_ACCOUNT
                  ? authStrings.signupAccountActivationMessage
                  : authStrings.signupErrorMessage
              }
              className="rc-error"
            />
          </Alert>
        </div>
      )}

      <div className="flex justify-center mt-10">
        <Button
          type="submit"
          variant="primary"
          color="red"
          disabled={isLoading || isDisabled}
          className="min-w-[260px]"
        >
          {isLoading ? strings.buttonSubmitting : authStrings.signupSubmit}
        </Button>
      </div>
    </form>
  )
}

export default SignupForm
